import { useMemo, useState } from "react";
import type { ProjectDetail } from "@/mocks/admin/projects";

type Props = { items: ProjectDetail["items"] };

export default function ItemsList({ items }: Props) {
  const categories = useMemo(() => {
    const set = new Set(items.map((i) => i.category));
    return ["All", ...Array.from(set)];
  }, [items]);

  const [activeCat, setActiveCat] = useState("All");

  const filtered = activeCat === "All" ? items : items.filter((i) => i.category === activeCat);
  const totalQty = items.reduce((s, i) => s + i.qty, 0);
  const totalCuft = items.reduce((s, i) => s + (i.cuft || 0) * i.qty, 0);
  const fragileCount = items.filter((i) => i.fragile).length;

  return (
    <div className="bg-white border border-ink-100 rounded-lg">
      <div className="px-5 pt-5 pb-4 border-b border-ink-100">
        <div className="flex items-center justify-between mb-1">
          <div>
            <h3 className="font-display text-base font-bold text-ink-900">Inventory</h3>
            <p className="text-[11px] text-ink-500 mt-0.5">
              {totalQty} items · {totalCuft} cu ft · {fragileCount} fragile
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button className="text-xs font-semibold text-brand-600 hover:text-brand-700 cursor-pointer whitespace-nowrap">
              + Add item
            </button>
            <button className="text-xs font-semibold text-ink-500 hover:text-ink-900 cursor-pointer whitespace-nowrap">
              Import list
            </button>
          </div>
        </div>

        <div className="flex items-center gap-1.5 flex-wrap mt-3">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setActiveCat(c)}
              className={`text-[11px] font-semibold px-2.5 py-1 rounded-full cursor-pointer whitespace-nowrap transition-colors ${
                activeCat === c
                  ? "bg-ink-900 text-white"
                  : "bg-cream-100 text-ink-600 hover:bg-ink-100"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      <ul className="divide-y divide-ink-100 max-h-96 overflow-y-auto scrollbar-thin">
        {filtered.map((i) => (
          <li key={i.id} className="px-5 py-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-cream-100 text-ink-500 flex items-center justify-center shrink-0">
              <i className="ri-box-3-line"></i>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-ink-900 truncate">{i.name}</div>
              <div className="text-[11px] text-ink-500 flex items-center gap-2 flex-wrap">
                <span>{i.category}</span>
                {i.cuft && <span>· {i.cuft} cu ft</span>}
                {i.fragile && (
                  <span className="inline-flex items-center gap-1 text-rose-600 font-semibold">
                    <i className="ri-alert-line text-[10px]"></i>
                    Fragile
                  </span>
                )}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-sm font-bold text-ink-900">×{i.qty}</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}